import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileSpreadsheet, Scale } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const fmtVES = (n) => `VES ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Conciliación banco-vs-ledger: pega el CSV del extracto de Banco Plaza y
// desglosa la diferencia en fees pendientes de registrar, créditos sin orden y
// cobros que el banco confirmó pero nunca acreditó.
export default function BankReconciliation() {
  const [merchants, setMerchants] = useState([]);
  const [merchantId, setMerchantId] = useState('');
  const [csvText, setCsvText] = useState('');
  const [bankBalance, setBankBalance] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/ledger/balances`, { headers: headers() })
      .then((res) => {
        const list = res.data.balances || [];
        setMerchants(list);
        if (list.length) setMerchantId(list[0].merchant_id);
      })
      .catch(() => {});
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsvText(String(r.result || ''));
    r.readAsText(f);
  };

  const run = async () => {
    setRunning(true); setErr(null); setResult(null);
    try {
      const res = await axios.post(`${API}/reconciliation/bank-csv`, {
        merchant_id: merchantId,
        csv_text: csvText,
        bank_balance: bankBalance === '' ? null : Number(bankBalance),
      }, { headers: headers() });
      setResult(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'No se pudo conciliar');
    } finally {
      setRunning(false);
    }
  };

  const Row = ({ label, value, color }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-600">{label}</span>
      <span className={`text-sm font-mono font-semibold ${color || 'text-gray-900'}`}>{value}</span>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border p-5 mb-6" data-testid="bank-reconciliation-panel">
      <div className="flex items-center gap-2 mb-1">
        <Scale className="w-5 h-5 text-blue-700" />
        <h3 className="text-lg font-bold" style={{ color: '#051C2C' }}>Conciliación banco vs ledger</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Carga el extracto de Banco Plaza (CSV con columnas
        <span className="font-mono"> Fecha,Hora,Naturaleza,Monto,Referencia,Concepto,Tipo</span>)
        y te desglosa la diferencia contra el ledger.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Comercio</label>
          <select
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            data-testid="reconciliation-merchant"
          >
            {merchants.map((m) => (
              <option key={m.merchant_id} value={m.merchant_id}>{m.company_name || m.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Saldo del banco (opcional)</label>
          <input
            value={bankBalance}
            onChange={(e) => setBankBalance(e.target.value)}
            placeholder="1327319.93"
            className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
            data-testid="reconciliation-bank-balance"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Extracto CSV</label>
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="w-full text-xs py-2" data-testid="reconciliation-file" />
        </div>
      </div>

      <textarea
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        rows={4}
        placeholder="…o pega aquí el contenido del CSV"
        className="w-full px-3 py-2 border rounded-lg text-xs font-mono mb-3"
        data-testid="reconciliation-csv-text"
      />

      <div className="flex items-center gap-2">
        <button
          onClick={run}
          disabled={running || !merchantId || csvText.trim().length < 20}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-40 flex items-center gap-2"
          data-testid="reconciliation-run"
        >
          <FileSpreadsheet className="w-4 h-4" /> {running ? 'Conciliando…' : 'Conciliar'}
        </button>
        {err && <span className="text-xs text-red-600" data-testid="reconciliation-error">{err}</span>}
      </div>

      {result && (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="reconciliation-result">
          <div className="border rounded-lg px-4 py-3 bg-gray-50">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
              Extracto {result.period.from} → {result.period.to}
            </p>
            <Row label={`Créditos (${result.bank_statement.credits_count})`} value={fmtVES(result.bank_statement.credits_total)} color="text-green-700" />
            <Row label={`Débitos (${result.bank_statement.debits_count})`} value={fmtVES(result.bank_statement.debits_total)} color="text-red-700" />
            <Row label={`Fees del banco (${result.bank_statement.fee_debits_count})`} value={fmtVES(result.bank_statement.fee_debits_total)} />
            <Row label="Neto del periodo" value={fmtVES(result.bank_statement.net)} strong />
          </div>

          <div className="border rounded-lg px-4 py-3 bg-gray-50">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Ledger mismo periodo</p>
            <Row label={`Pagos (${result.ledger.payment_in_count})`} value={fmtVES(result.ledger.payment_in_total)} color="text-green-700" />
            <Row label="Fees registrados" value={fmtVES(result.ledger.bank_fee_total)} />
            <Row label={`Reversas (${result.ledger.reversals_count})`} value={fmtVES(result.ledger.reversals_total)} color="text-red-700" />
            <Row label="Saldo del ledger" value={fmtVES(result.ledger.balance)} />
          </div>

          <div className="border rounded-lg px-4 py-3 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">De qué está hecha la diferencia</p>
            <Row label="Fees del banco pendientes de registrar" value={fmtVES(result.breakdown.fees_pendientes_de_registrar)} color="text-amber-700" />
            <Row
              label={`Créditos en el banco sin orden en Caura (${result.breakdown.creditos_sin_orden_count})`}
              value={fmtVES(result.breakdown.creditos_sin_orden_total)}
              color="text-blue-700"
            />
            <Row
              label={`Cobros confirmados y no acreditados (${result.breakdown.cobros_no_acreditados_count})`}
              value={fmtVES(result.breakdown.cobros_no_acreditados_total)}
              color="text-red-700"
            />
            {result.breakdown.bank_vs_ledger !== null && (
              <Row label="Banco − ledger (al instante del corte de datos)" value={fmtVES(result.breakdown.bank_vs_ledger)} />
            )}
          </div>

          {result.creditos_sin_orden.length > 0 && (
            <div className="border rounded-lg overflow-x-auto lg:col-span-2" data-testid="reconciliation-orphans">
              <p className="px-4 py-2 text-xs font-semibold bg-blue-50">Créditos sin orden en Caura</p>
              <table className="w-full text-xs">
                <tbody className="divide-y">
                  {result.creditos_sin_orden.map((c, i) => (
                    <tr key={`${c.referencia}-${i}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap">{c.fecha} {c.hora}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtVES(c.monto)}</td>
                      <td className="px-3 py-1.5 font-mono">{c.referencia}</td>
                      <td className="px-3 py-1.5 text-gray-600">{c.concepto}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.cobros_no_acreditados.length > 0 && (
            <div className="border rounded-lg overflow-x-auto lg:col-span-2" data-testid="reconciliation-unsettled">
              <p className="px-4 py-2 text-xs font-semibold bg-red-50">Cobros confirmados por el banco y no acreditados</p>
              <table className="w-full text-xs">
                <tbody className="divide-y">
                  {result.cobros_no_acreditados.map((c) => (
                    <tr key={c.order_code}>
                      <td className="px-3 py-1.5 font-mono">{c.order_code}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtVES(c.amount)}</td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{c.endtoend}</td>
                      <td className="px-3 py-1.5">
                        {c.en_extracto_por_referencia
                          ? <span className="text-amber-700">revisar: hay un movimiento con referencia parecida</span>
                          : <span className="text-red-700">sin crédito en el extracto</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
